import type { ReactNode } from 'react';
import { Tag, Tooltip } from 'antd';
import { EyeInvisibleOutlined, FileTextOutlined } from '@ant-design/icons';
import type { EvidenceView } from '@/api/types';
import { formatDay } from '@/lib/time';
import { evidenceFields, evidenceKind, FAMILY_VIEWERS, familyOf, fromRun, isDeclaredParty } from './evidenceModel';

function humanize(key: string): string {
  const leaf = key.split('.').pop() ?? key;
  const words = leaf.replace(/_/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase();
  return /^\d+$/.test(leaf) ? key : words.charAt(0).toUpperCase() + words.slice(1);
}

/** One evidence record through its family's viewer (OL-26). Erased records show the fact only. */
export function FamilyEvidenceCard({
  evidence,
  runId,
  finding,
  oneReviewer,
  awaiting,
  focused,
  action,
}: {
  evidence: EvidenceView;
  runId?: string;
  /** The latest analyst finding on this record, from the case's decision history. */
  finding?: 'ACCEPTED' | 'REJECTED' | null;
  /** The finding was recorded by one person, before overrides needed a second reviewer. */
  oneReviewer?: boolean;
  /** A proposed finding no one has approved yet. It has no effect. */
  awaiting?: 'ACCEPTED' | 'REJECTED' | null;
  /** A suggestion's reference opened this record. */
  focused?: boolean;
  action?: ReactNode;
}) {
  const family = familyOf(evidence);
  const viewer = family ? FAMILY_VIEWERS[family] : null;
  const kind = evidenceKind(evidence);
  const fields = evidenceFields(evidence, viewer);
  return (
    <div
      id={`evidence-${evidence.contentHash}`}
      className="ol-card"
      style={{ padding: '12px 14px', ...(focused ? { outline: '2px solid var(--ol-accent, #6d4aff)', outlineOffset: 1 } : {}) }}
      data-testid="evidence-card"
      data-family={family ?? 'generic'}
      data-focused={focused ? 'true' : undefined}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center' }}>
        <span style={{ fontWeight: 600, fontSize: 12.5, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <FileTextOutlined aria-hidden />
          {kind ?? evidence.mediaType ?? 'Evidence'}
        </span>
        <span>
          {finding && (
            <Tag bordered={false} color={finding === 'REJECTED' ? 'error' : 'success'} data-testid="evidence-finding">
              {finding === 'REJECTED' ? 'Rejected by analyst' : 'Accepted by analyst'}
              {oneReviewer ? ' (one reviewer)' : ''}
            </Tag>
          )}
          {awaiting && (
            <Tag bordered={false} color="warning" data-testid="evidence-awaiting">
              {awaiting === 'REJECTED' ? 'Reject' : 'Accept'} awaiting second reviewer
            </Tag>
          )}
          {isDeclaredParty(evidence) && (
            <Tag bordered={false} color="blue" data-testid="evidence-declared">
              Declared by the applicant
            </Tag>
          )}
          {runId && fromRun(evidence, runId) && <Tag bordered={false}>This run</Tag>}
          {!evidence.redacted && action}
          {evidence.redacted && (
            <Tooltip title="The subject's data was erased. The record and its hash remain as proof it existed.">
              <Tag bordered={false} color="warning" data-testid="evidence-erased">
                <EyeInvisibleOutlined aria-hidden /> Erased
              </Tag>
            </Tooltip>
          )}
        </span>
      </div>
      <div style={{ fontSize: 11.5, color: 'var(--ol-text-secondary)', marginTop: 5 }}>
        {[evidence.source, evidence.capturedAt ? `captured ${formatDay(evidence.capturedAt)}` : null]
          .filter(Boolean)
          .join(' · ')}
      </div>
      {fields.length > 0 && (
        <dl style={{ display: 'grid', gridTemplateColumns: 'minmax(120px, 200px) 1fr', gap: '3px 12px', margin: '10px 0 0', fontSize: 12.5 }}>
          {fields.map((f) => (
            <div key={f.key} style={{ display: 'contents' }}>
              <dt style={{ color: 'var(--ol-text-secondary)' }}>{humanize(f.key)}</dt>
              <dd style={{ margin: 0, wordBreak: 'break-word' }}>{f.value}</dd>
            </div>
          ))}
        </dl>
      )}
      {evidence.contentHash && (
        <div className="ol-mono" style={{ fontSize: 10.5, color: 'var(--ol-text-tertiary)', marginTop: 8, wordBreak: 'break-all' }} title={evidence.contentHash}>
          {evidence.contentHash}
        </div>
      )}
    </div>
  );
}
